import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import SEOHead from '../components/SEOHead';

export default function SoftwareDevelopmentPage() {
  const router = useRouter();
  
  // English-only page - canonical URL is fixed
  const canonicalUrl = '/software-development';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Software Development',
    description: 'Professional software development services in Lithuania. Custom software solutions built with modern technologies for businesses seeking digital transformation.',
    provider: {
      '@type': 'Organization',
      name: 'LB Visible',
      url: 'https://www.lbvisible.com',
      knowsAbout: [
        'Software Development',
        'Application Development',
        'Custom Software Solutions',
        'Digital Transformation',
        'Enterprise Software',
      ],
    },
    areaServed: {
      '@type': 'Country',
      name: 'Lithuania',
    },
  };

  return (
    <>
      <SEOHead
        title='Software Development | LBVisible'
        description='Professional software development services in Lithuania. Custom software solutions built with modern technologies for businesses seeking digital transformation.'
        keywords='software development, application development, custom software solutions, software development Lithuania, enterprise software'
        canonicalUrl={canonicalUrl}
        lang='en'
        structuredData={structuredData}
      />

      <main className='min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-12'>
        <article className='prose prose-lg dark:prose-invert max-w-none'>
          <style jsx global>{`
            article h1 {
              font-size: 2.25rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1.5rem;
              line-height: 1.2;
            }
            article h2 {
              font-size: 1.875rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1rem;
              line-height: 1.3;
            }
            article h3 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              line-height: 1.4;
            }
            article p {
              margin-bottom: 1.5rem;
              line-height: 1.75;
            }
          `}</style>
          <h1 className='text-gray-900 dark:text-white'>
            Software Development and Application Programming
          </h1>
          
          <h2 className='text-gray-900 dark:text-white'>
            Why Professional Software Development Matters
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Software development is the foundation of digital transformation for modern businesses. Custom software solutions enable companies to automate processes, improve efficiency, and gain competitive advantages. Professional software development ensures your applications are reliable, secure, scalable, and aligned with your business objectives. Whether you need desktop applications, mobile apps, or cloud-based solutions, professional development is essential for success.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Our Software Development Process
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Our software development process is designed to deliver high-quality solutions that meet your business needs. We start by understanding your requirements, analyzing your workflows, and identifying opportunities for improvement. We then design the software architecture, select appropriate technologies, and develop custom solutions using best practices and industry standards. Throughout development, we maintain clear communication, provide regular updates, and ensure quality through testing and code reviews.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            Custom Application Development
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We develop custom applications tailored to your specific business needs. Our application development services cover various platforms and technologies, ensuring your software works seamlessly across different environments. We focus on creating intuitive user interfaces, robust functionality, and reliable performance.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            Enterprise Software Solutions
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            For larger organizations, we develop enterprise software solutions that integrate with existing systems and support complex business processes. Our enterprise software development services include system integration, data migration, and ongoing support to ensure your software continues to meet your evolving needs.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Why Choose LBVisible for Software Development
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Choosing the right software development partner is crucial for achieving your business goals. At LBVisible, we combine technical expertise with business understanding to deliver software solutions that drive results. We use modern technologies, follow best practices, and maintain high standards of quality throughout the development process.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Our software development services include custom development, system integration, API development, database design, and ongoing maintenance. We work closely with you to ensure your software meets your requirements and supports your business objectives effectively.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Whether you need a new application, want to modernize existing software, or require integration services, we have the expertise to help you succeed. Contact us today to discuss your software development needs and discover how we can help your business achieve digital transformation.
          </p>
        </article>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  return {
    props: {},
  };
};

